import { type LocationSearchResult, type Image } from '@/api/accommodation/types'
import DurationDate from '@/components/dates/durationDate'
import { Badge } from '@/components/ui/badge'
import { Link, useRouterState } from '@tanstack/react-router'
import 'react-loading-skeleton/dist/skeleton.css'
import Discount from '@/components/money/discount'
import Price from '@/components/money/price'
import { ImagePlaceholder } from '@/components/ui/ImagePlaceholder'
import { getAccommodationTransitionName } from '@/lib/viewTransitions'
import { trackOfferClicked } from '@/lib/track'
import { unitFullName, unitPhotos } from '@/lib/unit'
import { AggregatedRatingBadge } from '@/components/rating/AggregatedRatingBadge'
import { ArrowLeftRightIcon, Gem } from 'lucide-react'
import { WatchHeartButton } from '@/features/accommodation/watch-heart/WatchHeartButton'

interface CardProps {
  searchResult?: LocationSearchResult
  position?: number
  source?: 'list' | 'landing' | 'similar'
  /** How a similar offer differs from the search, one badge each. */
  differences?: string[]
  /** Types of the differences, tracked with the click. */
  differenceTypes?: string[]
}

const MvImage = ({ images }: { images?: Image[] }) => {
  const firstImage = images && images.length > 0 ? images.sort((a, b) => b.priority - a.priority)[0] : null

  return (
    <>
      {firstImage ? (
        <img
          className="h-full w-full object-cover object-center"
          src={firstImage.url.thumbnail}
          alt={firstImage.description}
        />
      ) : (
        <ImagePlaceholder className="h-full w-full rounded-xl" />
      )}
    </>
  )
}

const SearchResultCard = ({ searchResult, position, source = 'list', differences, differenceTypes }: CardProps) => {
  const currentHref = useRouterState({ select: (s) => s.location.href })

  if (!searchResult) {
    if (import.meta.env.DEV) {
      console.warn('SearchResultCard rendered without searchResult data')
    }
    return null
  }

  const transitionName = getAccommodationTransitionName(searchResult.unit.id)

  // A real <a href> (not a div with onClick) so crawlers can follow the card
  // from the server-rendered landing and search pages to the detail.
  return (
    <Link
      to="/ubytovani/$slug/$unitSlug"
      params={{
        slug: searchResult.location.slug,
        unitSlug: searchResult.unit.slug,
      }}
      viewTransition
      state={{ backHref: currentHref }}
      className="grid h-100 cursor-pointer grid-cols-1 grid-rows-1 overflow-clip rounded-xl transition-opacity hover:opacity-90"
      style={{ viewTransitionName: transitionName }}
      onClick={() => trackOfferClicked(searchResult, position, source, differenceTypes)}
      aria-label={`Zobrazit detail: ${unitFullName(searchResult.location, searchResult.unit)}`}
    >
      <div className="col-start-1 row-start-1 flex items-center justify-center">
        <MvImage images={unitPhotos(searchResult.unit, searchResult.location)} />
      </div>
      <div className="col-start-1 row-start-1 bg-linear-to-b from-transparent from-50% to-[rgba(30,30,30,0.7)] to-90%"></div>
      <div className="col-start-1 row-start-1 grid h-full w-full grid-rows-[min-content_1fr_min-content_min-content] p-3">
        <div>
          {differences && differences.length > 0 && (
            <div className="-mx-3 -mt-3 mb-3 flex items-start gap-1.5 bg-amber-300 px-3 py-2 text-sm leading-snug font-semibold text-amber-900">
              <ArrowLeftRightIcon className="mt-0.5 size-4 shrink-0" />
              <span>{differences.join(' · ')}</span>
            </div>
          )}
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Discount minPrice={searchResult.minDiscountPrice} maxPrice={searchResult.standardPrice} />
              <Badge variant="secondary">{searchResult.unit.type.name}</Badge>
              {searchResult.rareFind && (
                <Badge variant="rare">
                  <Gem />
                  Vzácný objev
                </Badge>
              )}
            </div>
            <div className="flex shrink-0 items-start gap-1">
              <AggregatedRatingBadge sites={searchResult.location.sites} />
              <WatchHeartButton
                slug={searchResult.location.slug}
                unitId={searchResult.unit.id}
                source={source}
                className="-mt-1 -mr-1"
              />
            </div>
          </div>
        </div>

        <div></div>
        <div className="mb-2">
          <div className="text-xl font-semibold text-white">
            {unitFullName(searchResult.location, searchResult.unit)}
          </div>
          <div className="text-sm text-white/70">{searchResult.location.region.name}</div>
        </div>
        <div className="w-full space-y-0.5 rounded-lg border border-slate-100/20 bg-[#1E1E1E]/20 px-3 py-2 text-sm text-white backdrop-blur-[2px]">
          <DurationDate dateFrom={searchResult.start} dateTo={searchResult.end} />
          {searchResult.minNights >= 2 && (
            <span className="text-xs text-white/70">
              (min. {searchResult.minNights} {searchResult.minNights < 5 ? 'noci' : 'nocí'})
            </span>
          )}
          <div className="flex items-end justify-between">
            <div className="grow text-xl font-medium">
              {searchResult.minDiscountPrice !== searchResult.maxDiscountPrice && `od `}
              <Price price={searchResult.minDiscountPrice} />
              &nbsp;/&nbsp;noc
            </div>
            <div className="text-nowrap text-white/70">
              &nbsp;
              <Price price={searchResult.standardPrice} className="line-through" />
              &nbsp;/&nbsp;noc
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

export default SearchResultCard
